import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, Search } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useApiMessages } from "../../hooks/useApiMessages";
import { useVisitorScope } from "../../components/visitors/useVisitorScope";
import { PageHeader, ScopeFilters, StatusBadge } from "../../components/visitors/VisitorShared";
import { VisitDetailDialog } from "../../components/visitors/VisitDetailDialog";
import {
  dayRangeIso,
  exportToExcel,
  exportToPdf,
  fmtDateTime,
  fmtDuration,
  todayInput,
  visitExportColumns,
  visitorApi,
} from "../../components/visitors/visitorUtils";

const weekAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
};

export default function VisitHistory() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const scope = useVisitorScope();
  const [visits, setVisits] = useState([]);
  const [from, setFrom] = useState(weekAgo());
  const [to, setTo] = useState(todayInput());
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailId, setDetailId] = useState(() => searchParams.get("visit"));

  const load = useCallback(async () => {
    try {
      setVisits(
        await visitorApi.get("/visits", { ...scope.params, ...dayRangeIso(from, to), status, search, limit: 1000 })
      );
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.companyId, scope.facilityId, from, to, status, search]);

  useEffect(() => {
    const handle = setTimeout(load, 300);
    return () => clearTimeout(handle);
  }, [load]);

  const filename = `visitas_${from}_${to}`;
  const title = `${t("visitors.history.title")} (${from} – ${to})`;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("visitors.history.title")}
        subtitle={t("visitors.history.subtitle")}
        actions={
          <>
            <Button variant="outline" disabled={!visits.length} onClick={() => exportToExcel(visits, visitExportColumns(t), filename, title)}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button
              variant="outline"
              disabled={!visits.length}
              onClick={() => exportToPdf({ title, columns: visitExportColumns(t), rows: visits, filename })}
            >
              <FileDown className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </>
        }
      />

      <ScopeFilters scope={scope}>
        <div>
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.dateFrom")}</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.dateTo")}</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div>
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.status")}</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("visitors.preauths.filters.all")}</SelectItem>
              {["inside", "completed", "denied"].map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`visitors.status.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2 lg:col-span-1">
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.search")}</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder={t("visitors.common.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </ScopeFilters>

      <Card>
        <CardContent className="p-0 sm:p-2">
          {visits.length === 0 ? (
            <p className="text-center py-10 text-slate-500">{t("visitors.history.noVisits")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("visitors.common.visitor")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("visitors.common.facility")}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t("visitors.common.host")}</TableHead>
                  <TableHead>{t("visitors.common.checkIn")}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t("visitors.common.checkOut")}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t("visitors.common.guard")}</TableHead>
                  <TableHead>{t("visitors.common.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visits.map((v) => (
                  <TableRow key={v.id} className="cursor-pointer" onClick={() => setDetailId(v.id)}>
                    <TableCell>
                      <p className="font-medium">{v.visitor_name}</p>
                      <p className="text-xs text-slate-500">{[v.visitor_company, v.vehicle?.plate].filter(Boolean).join(" · ")}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{v.facility_name}</TableCell>
                    <TableCell className="hidden lg:table-cell">{v.host_name || "—"}</TableCell>
                    <TableCell className="text-sm">{fmtDateTime(v.check_in_at || v.created_at)}</TableCell>
                    <TableCell className="hidden sm:table-cell text-sm">
                      {fmtDateTime(v.check_out_at)}
                      {v.duration_minutes != null && <p className="text-xs text-slate-500">{fmtDuration(v.duration_minutes)}</p>}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{v.check_in_by_name}</TableCell>
                    <TableCell>
                      <StatusBadge status={v.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <VisitDetailDialog
        visitId={detailId}
        open={!!detailId}
        onOpenChange={(o) => {
          if (o) return;
          setDetailId(null);
          if (searchParams.has("visit")) setSearchParams({}, { replace: true });
        }}
        scope={scope}
        onChanged={load}
      />
    </div>
  );
}
